import './MMM.scss';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page/Page';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { useSetError } from '../../redux/modals';
import { awaitJSON, MetricsLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { ScenarioPlanning } from '../GeoMMM/ScenarioPlanning/ScenarioPlanning';
import { FAQ } from './FAQ/FAQ';
import { useFeatureMapping } from './hooks/useFeatureMapping';
import {
  DateOption,
  FormattedModelRunOptions,
  TINUITI_TEST_HOME_SERVICES,
  TINUITI_TEST_HOME_SERVICES_PROXY
} from './mmm-utils';
import { MmmVariantConfig } from './mmm-variant-constants';
import { MmmRunSelector } from './MmmRunSelector/MmmRunSelector';
import {
  markRunsWithoutScenarioModel,
  rmmmRowModelToSpModel,
  toModelRuns
} from './MmmRunSelector/mmm-run-selector-adapters';
import { ModelResults } from './ModelResults/ModelResults';
import { OfflineInputs } from './OfflineInputs/OfflineInputs';
import { useMmmPage } from './use-mmm-page';

const TabKey = {
  MODEL_RESULTS: 'model-results',
  SCENARIO_PLANNING: 'scenario-planning',
  FAQ: 'faq',
  OFFLINE_INPUTS: 'offline-inputs'
} as const;
type TabKeyType = (typeof TabKey)[keyof typeof TabKey];

export const MmmVariantPage = ({ config }: { config: MmmVariantConfig }) => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const company =
    cid === TINUITI_TEST_HOME_SERVICES ? TINUITI_TEST_HOME_SERVICES_PROXY : (cid ?? '');
  // Lives at the page top so any tab can consume the map; only Scenario Planning uses it today.
  // Keyed on the raw cid (not the proxy) to match the cid ScenarioPlanning fetches models with.
  const { data: featureMapping } = useFeatureMapping(cid ?? '', config.scenarioModelType);
  const baseURL = config.key;
  const { tab, goToTab, navs, isInternal } = useMmmPage({
    baseURL,
    navs: [
      { key: TabKey.MODEL_RESULTS, label: 'Model Results' },
      { key: TabKey.SCENARIO_PLANNING, label: 'Scenario Planning', requiresInternal: true },
      { key: TabKey.FAQ, label: 'FAQ' },
      { key: TabKey.OFFLINE_INPUTS, label: 'Offline Inputs', requiresInternal: true }
    ]
  });
  const [kpi, setKpi] = useState<string>('');
  const [groupBy, setGroupBy] = useState('channel');
  const [groupByOptions, setGroupByOptions] = useState<{ label: string; value: string }[]>([]);
  const disabledPlatform = useMemo(() => {
    if (groupByOptions.length > 0) {
      return groupByOptions.filter(option => option.value === 'platform').length === 0;
    }
    return undefined;
  }, [groupByOptions]);
  const [pageActions, setPageActions] = useState<ReactElement | undefined>(undefined);
  const [pageArchiveActions, setPageArchiveActions] = useState<ReactElement | undefined>(undefined);
  const [currentModelRun, setCurrentModelRun] = useState<FormattedModelRunOptions>();
  const [modelRunOptions, setModelRunOptions] = useState<FormattedModelRunOptions[]>([]);
  const [showActualizedData, setShowActualizedData] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  // The company and variant whose run list is loaded, so a tab that already has it never refetches.
  const loadedRunListKey = useRef<string | undefined>(undefined);
  const selectedDate = useMemo(
    () => (currentModelRun ? currentModelRun.date : ''),
    [currentModelRun]
  );
  const kpiRuns = useMemo(
    () => toModelRuns(modelRunOptions, { isInternal }),
    [modelRunOptions, isInternal]
  );
  // On the Scenario Planning tab the same picker marks runs that have no scenario model.
  const selectorKpiRuns = useMemo(
    () =>
      tab === TabKey.SCENARIO_PLANNING
        ? markRunsWithoutScenarioModel(
            kpiRuns,
            run =>
              !!modelRunOptions
                .find(option => option.date === run.refreshDate)
                ?.kpis.find(option => option.value === run.kpi)?.scenarioPlanning
          )
        : kpiRuns,
    [tab, kpiRuns, modelRunOptions]
  );
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const toggleMMMRun = useCallback(
    async (modelRun: FormattedModelRunOptions, currKpi: string, checked: boolean) => {
      try {
        setModelRunOptions(m =>
          m.map(row => {
            if (row.date === modelRun.date) {
              const newRow = { ...row };
              const otherKpisOn = row.kpis.some(
                kpiOption => kpiOption.value !== currKpi && kpiOption.client_facing
              );
              newRow.client_facing = checked || otherKpisOn;
              newRow.kpis = row.kpis.map(kpiOption =>
                kpiOption.value === currKpi
                  ? { ...kpiOption, client_facing: checked }
                  : { ...kpiOption }
              );
              // Toggling another run from the run menu mustn't change the selection.
              setCurrentModelRun(curr => (curr?.date === newRow.date ? { ...newRow } : curr));
              return { ...newRow };
            }
            return { ...row };
          })
        );
        await MetricsLambdaFetch('/toggleMMMRun', {
          method: 'POST',
          body: {
            company,
            date: modelRun.date,
            client_facing: checked,
            branch: modelRun.branch,
            kpi: currKpi
          }
        });
      } catch (e) {
        setError({
          message: e.message,
          reportError: e
        });
      }
    },
    [company, setError]
  );

  useEffect(() => {
    const getDateOptions = async () => {
      // Offline Inputs and FAQ don't consume dateOptions; skipping it avoids a refetch that would
      // reset the user's selected model run / KPI when they visit those tabs and return.
      if (([TabKey.OFFLINE_INPUTS, TabKey.FAQ] as TabKeyType[]).includes(tab)) {
        return;
      }
      // Scenario Planning reads each run's model off the run list, so a direct visit must load it,
      // but once it is loaded the user's selected run / KPI is kept.
      const runListKey = `${company}|${config.modelType ?? ''}`;
      if (tab === TabKey.SCENARIO_PLANNING && loadedRunListKey.current === runListKey) {
        return;
      }

      const filterDateRuns = (options: DateOption[]) => {
        const filterByRelease = isInternal ? options : options.filter(obj => obj.client_facing);
        const formattedRuns = filterByRelease
          .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
          .reduce((acc: FormattedModelRunOptions[], cv, i) => {
            if (i > 0 && acc[acc.length - 1].date === cv.date) {
              const lastInd = acc.length - 1;
              acc[lastInd].client_facing = acc[lastInd].client_facing || cv.client_facing;

              if (!acc[lastInd].kpis.find(kpiOption => kpiOption.value === cv.kpi)) {
                acc[lastInd].kpis.push({
                  value: cv.kpi,
                  label: cv.kpi.replace(/_/g, ' '),
                  client_facing: cv.client_facing,
                  realKpi: cv.kpi,
                  scenarioPlanning: cv.scenarioPlanning
                });
              }
            } else {
              const currPrettyDate = new Date(cv.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              });
              let currExtraLabel = 0;
              if (i > 0 && acc[acc.length - 1].prettyDate === currPrettyDate) {
                if (!acc[acc.length - 1].extraLabel) {
                  acc[acc.length - 1].extraLabel = 1;
                }
                currExtraLabel = acc[acc.length - 1].extraLabel + 1;
              }
              acc.push({
                branch: cv.branch,
                date: cv.date,
                prettyDate: currPrettyDate,
                newest: i === 0,
                client_facing: cv.client_facing,
                output_file_version: cv.output_file_version,
                extraLabel: currExtraLabel,
                kpis: [
                  {
                    value: cv.kpi,
                    label: cv.kpi.replace(/_/g, ' '),
                    client_facing: cv.client_facing,
                    realKpi: cv.kpi,
                    scenarioPlanning: cv.scenarioPlanning
                  }
                ]
              });
            }
            return acc;
          }, []);
        return formattedRuns;
      };
      try {
        setLoading(true);
        // config.modelType filters to this variant's runs (DTC/Commerce rMMM splits); plain
        // rMMM has no modelType and shows everything. DEV-24802: the test company has no real
        // rMMM runs of its own, so plain rMMM also falls back to the DTC folder — without this
        // filter, /dateOptions would mix in the DTC/Commerce dashboard's runs too and rMMM would
        // auto-select whichever happened to be newest.
        const modelType = config.modelType ?? (companyEqualsTest ? 'dtc' : undefined);
        const res = await MetricsLambdaFetch('/dateOptions', {
          params: modelType ? { company, modelType } : { company }
        });
        const dateOptions = await awaitJSON<DateOption[]>(res);
        if (dateOptions.length < 1) {
          setError({
            message: 'There are no MMM runs for this client'
          });
          return;
        }
        const dateOptionsFormated = filterDateRuns(dateOptions);
        if (dateOptionsFormated.length === 0) {
          setError({
            message: 'There are no released MMM runs for this client'
          });
          return;
        }

        // For demo purposes, the fake label keeps `realKpi` pointing at this date's actual KPI
        // so the request sent to the backend still resolves to a real run for that date.
        if (companyEqualsTest) {
          dateOptionsFormated.forEach(dateOption => {
            const primaryKpi = dateOption.kpis[0];
            primaryKpi.value = 'Shopify_Revenue';
            primaryKpi.label = 'Shopify Revenue';
          });
        }

        const initialRun =
          dateOptionsFormated.find(run => run.client_facing === true) ||
          dateOptionsFormated[dateOptionsFormated.length - 1];
        loadedRunListKey.current = runListKey;
        setModelRunOptions(dateOptionsFormated);
        setCurrentModelRun(initialRun);
        const initialKpi =
          initialRun.kpis.find(kpiOption => kpiOption.client_facing) || initialRun.kpis[0];
        setKpi(initialKpi.value);
      } catch (e) {
        setError({
          message: e.message,
          reportError: e
        });
      } finally {
        setLoading(false);
      }
    };
    getDateOptions();
  }, [company, setError, tab, isInternal, companyEqualsTest, config.modelType]);

  // The page's selected run and its scenario model (carried on the run row): one selector drives both tabs.
  const sharedRun = useMemo(() => {
    if (!currentModelRun || !kpi) return undefined;
    const model = currentModelRun.kpis.find(option => option.value === kpi)?.scenarioPlanning;
    return { model: model && rmmmRowModelToSpModel(model) };
  }, [currentModelRun, kpi]);
  const runModels = useMemo(
    () =>
      modelRunOptions.flatMap(run =>
        run.kpis.flatMap(option =>
          option.scenarioPlanning ? [rmmmRowModelToSpModel(option.scenarioPlanning)] : []
        )
      ),
    [modelRunOptions]
  );

  useEffect(() => {
    const runSelector = currentModelRun && kpi && (
      <MmmRunSelector
        kpiRuns={selectorKpiRuns}
        kpi={kpi}
        selectedDate={selectedDate}
        isInternal={isInternal}
        onChange={selection => {
          const run = modelRunOptions.find(option => option.date === selection.run.refreshDate);
          if (!run) return;
          setCurrentModelRun(run);
          setKpi(selection.kpi);
        }}
        onToggleClientFacing={(run, clientFacing) => {
          const modelRun = modelRunOptions.find(option => option.date === run.refreshDate);
          if (modelRun) {
            toggleMMMRun(modelRun, run.kpi, clientFacing);
          }
        }}
      />
    );
    switch (tab) {
      case TabKey.MODEL_RESULTS:
        setPageActions(
          <div id='mmm-header-sub-actions'>
            <div id='mmm-archive-kpis'>{runSelector}</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              className='mmm-group-by-dropdown'
              stretch={StretchMode.FIT}
              onChange={setGroupBy}
              options={groupByOptions}
              value={groupBy}
            />
          </div>
        );
        setPageArchiveActions(
          <div className='primary-actions-container-mmm'>
            <SegmentedControlButton
              size={Size.LG}
              options={['Actualized', 'Raw']}
              selectedOption={showActualizedData ? 'Actualized' : 'Raw'}
              onChange={key => setShowActualizedData(key === 'Actualized')}
              className='mmm-effective-raw-toggle'
            />
          </div>
        );
        break;
      case TabKey.SCENARIO_PLANNING:
        setPageActions(
          <div id='mmm-header-sub-actions'>
            <div id='mmm-archive-kpis'>{runSelector}</div>
          </div>
        );
        setPageArchiveActions(undefined);
        break;
      default:
        setPageActions(undefined);
        setPageArchiveActions(undefined);
    }
  }, [
    groupBy,
    groupByOptions,
    kpi,
    tab,
    isInternal,
    currentModelRun,
    modelRunOptions,
    selectorKpiRuns,
    toggleMMMRun,
    selectedDate,
    showActualizedData
  ]);

  return (
    <Page
      actions={pageArchiveActions}
      navs={navs}
      onNav={nav => goToTab(nav as TabKeyType)}
      pageType={config.pageType}
      selectedNav={tab}
      title={config.title}
      subHeader={pageActions}
    >
      <div className='full-page-router mmm-page'>
        {tab === TabKey.MODEL_RESULTS && !loading && currentModelRun && kpi && (
          <ModelResults
            showActualizedData={showActualizedData}
            disabledPlatform={disabledPlatform}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            groupByOptions={groupByOptions}
            setGroupByOptions={setGroupByOptions}
            kpi={kpi}
            modelRun={currentModelRun}
          />
        )}
        {tab === TabKey.SCENARIO_PLANNING && !loading && sharedRun && (
          <ScenarioPlanning
            modelType={config.scenarioModelType}
            featureMapping={featureMapping}
            sharedRun={sharedRun}
            runModels={runModels}
          />
        )}
        {tab === TabKey.FAQ && <FAQ />}
        {tab === TabKey.OFFLINE_INPUTS && <OfflineInputs />}
      </div>
    </Page>
  );
};
